/**
 * Axios 请求拦截器配置
 *
 * 功能：
 * - 请求拦截：添加 token、语言等请求头
 * - 响应拦截：统一错误处理、token 刷新
 */
import type { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios'
import { ElMessage } from 'element-plus'
import { useUserStore } from '@/stores/user'
import i18n from '@/locales'
import router from '@/router'

/**
 * 配置 Axios 拦截器
 */
export function setupInterceptors(axiosInstance: AxiosInstance) {
  // 请求拦截器
  axiosInstance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const userStore = useUserStore()

      // 添加 JWT token
      if (userStore.token) {
        config.headers.Authorization = `Bearer ${userStore.token}`
      }

      // 添加语言标识（从 i18n 实例获取当前语言）
      config.headers['Accept-Language'] = i18n.global.locale.value

      return config
    },
    (error: AxiosError) => {
      console.error('[Request Error]:', error)
      return Promise.reject(error)
    }
  )

  // 响应拦截器
  axiosInstance.interceptors.response.use(
    (response) => {
      // 后端统一响应格式: {code, message, data}
      // 直接返回 data 字段
      const { data } = response

      // 如果响应有 code 字段，说明是标准格式，返回 data.data
      if (data && typeof data === 'object' && 'code' in data) {
        // code = 0 表示成功
        if (data.code === 0) {
          return data.data
        }
        // code != 0 表示业务错误
        const message = data.message || 'An error occurred'
        ElMessage.error(message)
        const err: any = new Error(message)
        err.responseData = data
        err.code = data.code
        err.data = data.data
        return Promise.reject(err)
      }

      // 非标准格式，直接返回
      return data
    },
    async (error: AxiosError<any>) => {
      const { response } = error

      if (!response) {
        ElMessage.error('Network error, please check your connection')
        return Promise.reject(error)
      }

      const { status, data } = response
      const userStore = useUserStore()

      // 根据状态码处理错误
      switch (status) {
        case 401:
          // Token 过期或无效
          if (data?.code === 'TOKEN_EXPIRED' && userStore.refreshToken) {
            // 尝试刷新 token
            try {
              await userStore.refreshAccessToken()
              // 重试原请求
              return axiosInstance.request(error.config!)
            } catch (refreshError) {
              // 刷新失败，退出登录
              userStore.logout()
              router.push('/login')
              ElMessage.error('Session expired, please login again')
              return Promise.reject(refreshError)
            }
          } else {
            // 未授权，跳转登录
            userStore.logout()
            router.push('/login')
            ElMessage.error('Please login first')
          }
          break

        case 403:
          ElMessage.error('You do not have permission to access this resource')
          break

        case 404:
          ElMessage.error('Resource not found')
          break

        case 500:
          ElMessage.error('Server error, please try again later')
          break

        default:
          // 显示后端返回的错误消息
          const message = data?.message || 'An error occurred'
          ElMessage.error(message)
      }

      return Promise.reject(error)
    }
  )
}
